import { Controller } from "@hotwired/stimulus"

// A brand's Production chart (brands/_production, Brand::Timeline): hovering
// its background shows which month you're over, and a click opens the brand's
// Timeline at that month (#YYYY-MM). The bars keep their own links.
//
//   <div data-controller="production-chart"
//        data-production-chart-first-month-value="2014-01"
//        data-production-chart-month-px-value="24"
//        data-production-chart-url-value="/brands/mezco-toyz/timeline">
//     <span data-production-chart-target="guide"></span>
//
// Following (the Timeline's panorama, pinned above the line), a click jumps
// the line on this page to the month instead, and the `here` window follows
// the month you're on as you scroll, the strip sliding sideways to keep it in view.
export default class extends Controller {
  static targets = ["guide", "chart", "here", "scroller"]
  static values = { firstMonth: String, monthPx: Number, url: String, follow: Boolean }

  static MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

  connect() {
    // A strip opens at its newest end.
    if (this.hasScrollerTarget) this.scrollerTarget.scrollLeft = this.scrollerTarget.scrollWidth
    if (!this.followValue) return
    this.boundFollow = () => {
      if (this.queued) return
      this.queued = true
      requestAnimationFrame(() => { this.queued = false; this.follow() })
    }
    window.addEventListener("scroll", this.boundFollow, { passive: true })
    window.addEventListener("resize", this.boundFollow)
    this.boundFollow()
  }

  disconnect() {
    if (!this.boundFollow) return
    window.removeEventListener("scroll", this.boundFollow)
    window.removeEventListener("resize", this.boundFollow)
  }

  move(event) {
    if (event.target.closest("a")) return this.hide()
    const { index, year, month } = this.monthAt(event)
    this.guideTarget.style.left = `${index * this.monthPxValue}px`
    this.guideTarget.style.width = `${this.monthPxValue}px`
    this.guideTarget.dataset.label = `${this.constructor.MONTHS[month - 1]} ${year}${this.followValue ? "" : " →"}`
    this.guideTarget.hidden = false
  }

  hide() {
    this.guideTarget.hidden = true
  }

  open(event) {
    if (event.target.closest("a")) return
    const { year, month } = this.monthAt(event)
    const key = `${year}-${String(month).padStart(2, "0")}`
    if (this.followValue) window.location.hash = key
    else window.location.href = `${this.urlValue}#${key}`
  }

  // The month at the middle of the window: the window sits on it, and the strip keeps it in view.
  // It's the last month whose heading has passed the line, so the gap between two months
  // still counts as the one above (looking for a month spanning the line found none there,
  // and fell back to the first month on the page: the window jumped to the strip's end and back).
  follow() {
    if (!this.hasHereTarget) return
    const line = window.innerHeight * 0.55
    const groups = Array.from(document.querySelectorAll(".timeline .timeline-date-group[data-date]"))
                        .filter(g => g.getBoundingClientRect().height > 0)
    const passed = groups.filter(g => g.getBoundingClientRect().top <= line)
    const group = passed[passed.length - 1] || groups[0]
    if (!group || group.dataset.date === this.followed) return
    this.followed = group.dataset.date
    const [y0, m0] = this.firstMonthValue.split("-").map(Number)
    const [y, m] = group.dataset.date.split("-").map(Number)
    const x = ((y - y0) * 12 + (m - m0)) * this.monthPxValue
    this.hereTarget.style.left = `${x}px`
    this.hereTarget.style.width = `${this.monthPxValue}px`
    this.hereTarget.hidden = false
    if (!this.hasScrollerTarget) return
    const s = this.scrollerTarget
    if (x < s.scrollLeft + 40 || x > s.scrollLeft + s.clientWidth - 80) s.scrollTo({ left: x - s.clientWidth / 2, behavior: "smooth" })
  }

  monthAt(event) {
    const chart = this.hasChartTarget ? this.chartTarget : this.element
    const x = event.clientX - chart.getBoundingClientRect().left
    const index = Math.max(0, Math.floor(x / this.monthPxValue))
    const [y0, m0] = this.firstMonthValue.split("-").map(Number)
    const total = (m0 - 1) + index
    return { index, year: y0 + Math.floor(total / 12), month: (total % 12) + 1 }
  }
}
